<shared-drive-selector>
  <div class="shared-drive-selector-container w-full flex flex-col gap-4">
    <!-- Header -->
    <div class="card bg-base-100 shadow-md border border-primary/20">
      <div class="card-body gap-2">
        <h3 class="card-title text-lg flex items-center gap-2">
          <i class="icon-folder-open text-xl text-primary"></i>
          {locale?.t('admin_drive_select_title')}
        </h3>
        <p class="text-sm text-base-content/80">
          {locale?.t(props.mode === 'folder' ? 'admin_drive_select_subtitle_folder' : 'admin_drive_select_subtitle')}
        </p>
      </div>
    </div>

    <!-- Loading State -->
    <div if="{state.loading}" class="flex justify-center py-12">
      <div class="flex flex-col items-center gap-3">
        <span class="loading loading-spinner loading-lg"></span>
        <p class="text-sm text-base-content/80">{locale?.t('admin_drive_loading')}</p>
      </div>
    </div>

    <!-- Error State -->
    <div if="{state.error && !state.loading}" class="alert alert-error">
      <i class="icon-warning text-xl"></i>
      <span class="text-sm">{state.error}</span>
      <button onclick="{retryLoadDrives}" class="btn btn-sm btn-ghost">
        <i class="icon-refresh"></i>
        Retry
      </button>
    </div>

    <!-- Search/Filter Toolbar -->
    <div if="{!state.loading && !state.error && state.allDrives.length > 0}" class="flex gap-2 items-center">
      <input
        id="shared-drive-search"
        type="search"
        placeholder="{locale?.t('admin_drive_search_placeholder')}"
        aria-label="{locale?.t('admin_drive_search_placeholder')}"
        class="input input-bordered input-sm flex-1"
        value="{state.searchQuery}"
        oninput="{(e) => handleSearch(e.target.value)}"
        onsearch="{(e) => handleSearch(e.target.value)}" />

      <button
        onclick="{clearFilter}"
        class="btn btn-sm btn-outline"
        disabled="{!state.searchQuery}">
        <i class="icon-clear me-1"></i>
        {locale?.t('clearfilter')}
      </button>
    </div>

    <!-- Drives List Container with Scrollable Area -->
    <div if="{!state.loading && !state.error && state.allDrives.length > 0}" class="drives-list-container flex flex-col gap-0">
      <!-- Scrollable Drives List -->
      <div class="drives-list-scroll space-y-2 overflow-y-auto mb-4" style="max-height: 400px;">
        <div each="{drive in state.drives}"
             role="button"
             tabindex="0"
             class="drive-item card shadow-sm border border-base-300 hover:border-primary transition-all cursor-pointer overflow-hidden relative group"
             onclick="{() => selectDrive(drive)}"
             onkeydown="{tools?.keyClick(() => selectDrive(drive))}"
             style="background-image: url({drive.backgroundImageLink}); background-size: cover; background-position: center; background-color: rgba(0, 0, 0, 0.1);">
        
        <!-- Background overlay for readability (darker on hover) -->
        <div class="drive-overlay absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"></div>
        
        <div class="card-body py-4 px-6 gap-3 relative z-10">
          <div class="flex items-start gap-4 w-full">
            <!-- Radio Button -->
            <div class="flex items-center pt-1">
              <input
                id="shared-drive-selector-radio-{drive.id}"
                type="radio"
                name="sharedDrive"
                class="radio radio-primary radio-md"
                aria-label="{drive.name}"
                checked="{state.selectedDrive?.id === drive.id}"
                onchange="{() => selectDrive(drive)}" />
            </div>

            <!-- Drive Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <h4 class="font-semibold text-base text-white drop-shadow-lg">{drive.name}</h4>
                <span if="{drive.isDefault}" class="badge badge-primary badge-sm">My Drive</span>
              </div>
              <p if="{drive.createdTime}" class="text-xs text-white/80 drop-shadow">
                Created: {tools?.formatDate(drive.createdTime)}
              </p>
              
              <!-- Capabilities -->
              <div if="{drive.capabilities}" class="flex gap-2 mt-2">
                <span if="{drive.capabilities.canCopy}" class="badge badge-outline badge-sm bg-white/90 text-black border-white/50">
                  <i class="icon-copy me-1"></i>{locale?.t('admin_drive_capabilities_copy')}
                </span>
                <span if="{drive.capabilities.canDownload}" class="badge badge-outline badge-sm bg-white/90 text-black border-white/50">
                  <i class="icon-download me-1"></i>{locale?.t('admin_drive_capabilities_download')}
                </span>
                <span if="{drive.capabilities.canEdit}" class="badge badge-outline badge-sm bg-white/90 text-black border-white/50">
                  <i class="icon-metalark-edit me-1"></i>{locale?.t('admin_drive_capabilities_edit')}
                </span>
              </div>
            </div>

            <!-- Drive Color Indicator (if available) -->
            <div if="{drive.colorRgb}"
                 class="w-12 h-12 rounded-lg border-2 border-white/50 flex-shrink-0 shadow-lg"
                 style="background-color: {drive.colorRgb}"></div>
          </div>
        </div>
      </div>
      </div>

    <!-- Empty State (no drives at all) -->
    <div if="{!state.loading && !state.error && state.allDrives.length === 0}" class="alert alert-warning">
      <i class="icon-error_outline text-xl"></i>
      <span class="text-sm">{locale?.t('admin_drive_nodrivesavailable')}</span>
    </div>

    <!-- No Search Results -->
    <div if="{!state.loading && !state.error && state.allDrives.length > 0 && state.drives.length === 0 && state.searchQuery}" class="card bg-base-100 mb-6">
      <div class="card-body text-center py-6">
        <i class="icon-search text-6xl text-gray-300 mb-2"></i>
        <p class="text-gray-500">No drives match "{state.searchQuery}"</p>
      </div>
    </div>

      <!-- Sticky Action Buttons (Fixed at bottom of list) -->
      <div class="sticky-action-buttons card bg-base-200 shadow-md border border-base-300 border-t-2">
        <div class="card-body py-4 px-6">
          <div class="flex items-center justify-between gap-4">
            <div class="text-sm text-base-content/80">
              <span if="{state.selectedDrive}">
                {locale?.t('selected')}: <span class="font-semibold ms-2">{state.selectedDrive.name}</span>
              </span>
              <span if="{!state.selectedDrive}" class="text-warning">{locale?.t('admin_drive_nodriveselected')}</span>
            </div>
            <button
              onclick="{confirmSelection}"
              class="btn btn-primary btn-sm"
              disabled="{!state.selectedDrive || state.savingSelection}">
              <i if="{!state.savingSelection}" class="icon-arrow_forward me-1"></i>
              <span if="{state.savingSelection}" class="loading loading-spinner loading-xs me-1"></span>
              {state.savingSelection ? locale?.t('saving') : locale?.t(props.mode === 'folder' ? 'browsefolders' : 'browsefiles')}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- OAuth Reauthorization Modal -->
    <div if="{state.showOAuthModal}" class="modal modal-open">
      <div class="modal-box">
        <h3 class="font-bold text-lg mb-4">
          <i class="icon-warning text-warning me-2"></i>
          Repository Connection Expired
        </h3>
        <p class="py-4">{state.oauthErrorMessage}</p>
        <div class="modal-action gap-2">
          <button
            onclick="{closeOAuthModal}"
            class="btn btn-ghost">
            {locale?.t('cancel')}
          </button>
          <button
            onclick="{reauthorizeRepository}"
            class="btn btn-primary">
            <i class="icon-refresh me-1"></i>
            Re-authorize Now
          </button>
        </div>
      </div>
      <button type="button" aria-label="{locale?.t('close')}" class="modal-backdrop" onclick="{closeOAuthModal}"></button>
    </div>
  </div>

  <script>
    import { getRepositoryService } from '../../../services/service-loader';

    export default {

      props: {
        repositoryId: null,
        provider: null,
        onDriveSelected: null,
        mode: 'file'
      },

      state: {
        drives: [],
        allDrives: [],
        selectedDrive: null,
        loading: false,
        error: null,
        savingSelection: false,
        showOAuthModal: false,
        oauthErrorMessage: '',
        oauthErrorCallback: null,
        searchQuery: ''
      },

      async onMounted() {
        if (this.props.repositoryId && this.props.provider === 'google_drive') {
          await this.loadSharedDrives();
        }
      },

      async loadSharedDrives() {
        try {
          this.update({ loading: true, error: null });

          // Use repository service to fetch shared drives
          const repositoryService = await getRepositoryService();
          const data = await repositoryService.getSharedDrives(this.props.repositoryId);
          
          const drives = data.drives || [];

          // Auto-select the first drive (usually "My Drive")
          const firstDrive = drives.length > 0 ? drives[0] : null;

          this.update({
            drives,
            allDrives: drives,
            selectedDrive: firstDrive,
            loading: false
          });
        } catch (error) {
          console.error('Failed to load Shared Drives:', error);
          
          // Check if it's an OAuth token expiration error
          if (error.response?.status === 401 && error.response?.data?.error === 'oauth_token_expired') {
            this.showOAuthExpiredModal(
              error.response.data.message || 'Your repository connection has expired.',
              () => this.loadSharedDrives()
            );
            this.update({ loading: false });
            return;
          }
          
          // If endpoint not found (404), skip drive selection and proceed to file browsing
          if (error.response?.status === 404) {
            console.warn('Shared drives endpoint not available. Proceeding to file browsing...');
            this.update({ loading: false });
            if (this.props.onDriveSelected) {
              this.props.onDriveSelected({
                driveId: 'root',
                driveName: 'My Drive'
              });
            }
            return;
          }
          
          // On other errors, proceed to file browsing with root/My Drive
          if (this.props.onDriveSelected) {
            this.props.onDriveSelected({
              driveId: 'root',
              driveName: 'My Drive'
            });
          }
          this.update({ loading: false });
        }
      },

      selectDrive(drive) {
        this.update({ selectedDrive: drive });
      },

      async confirmSelection() {
        if (!this.state.selectedDrive) {
          alert(this.locale.t('alert_sharepoint_select_required'));
          return;
        }

        try {
          this.update({ savingSelection: true });

          // Use dedicated Google Drive selection endpoint to avoid TSOA union validation issues
          const repositoryService = await getRepositoryService();
          const updateResponse = await repositoryService.selectGoogleDrive(this.props.repositoryId, {
            sharedDriveId: this.state.selectedDrive.id,
            sharedDriveName: this.state.selectedDrive.name
          });

          console.log('Shared drive selection saved:', {
            driveId: this.state.selectedDrive.id,
            driveName: this.state.selectedDrive.name,
            response: updateResponse
          });

          this.update({ savingSelection: false });

          // Notify parent component with full drive info
          if (this.props.onDriveSelected) {
            this.props.onDriveSelected({
              driveId: this.state.selectedDrive.id,
              driveName: this.state.selectedDrive.name,
              name: this.state.selectedDrive.name,
              displayName: this.state.selectedDrive.name,
              savedConfig: updateResponse
            });
          }
        } catch (error) {
          console.error('Failed to save Shared Drive selection:', error);
          
          // Check if it's an OAuth token expiration error
          if (error.response?.status === 401 && error.response?.data?.error === 'oauth_token_expired') {
            this.update({ savingSelection: false });
            this.showOAuthExpiredModal(
              error.response.data.message || 'Your repository connection has expired.',
              () => this.confirmSelection()
            );
            return;
          }

          alert(this.locale.t('alert_sharepoint_save_failed') + ': ' + (error.message || 'Unknown error'));
          this.update({ savingSelection: false });
        }
      },

      retryLoadDrives() {
        this.loadSharedDrives();
      },

      showOAuthExpiredModal(errorMessage, retryCallback) {
        // Show OAuth re-auth modal and store the callback to retry the original action
        this.update({
          showOAuthModal: true,
          oauthErrorMessage: errorMessage || 'Your repository connection has expired. Please re-authorize to continue.',
          oauthErrorCallback: retryCallback
        });
      },

      async reauthorizeRepository() {
        try {
          // Import the repository service to get OAuth URL
          const repositoryService = await getRepositoryService();

          // Get OAuth URL
          const oauthData = await repositoryService.getOAuthUrl(this.props.repositoryId);
          
          if (oauthData?.authorizationUrl) {
            // Create a promise that resolves when OAuth completes
            const oauthPromise = new Promise((resolve) => {
              const messageHandler = (event) => {
                // Verify message is from our origin
                if (event.origin !== window.location.origin) return;
                
                // Check for OAuth completion message
                if (event.data?.type === 'repository-connected') {
                  window.removeEventListener('message', messageHandler);
                  resolve(event.data);
                }
              };
              
              // Listen for postMessage from OAuth callback popup
              window.addEventListener('message', messageHandler);
              
              // Timeout after 5 minutes if no response
              setTimeout(() => {
                window.removeEventListener('message', messageHandler);
                resolve({ success: false, timeout: true });
              }, 5 * 60 * 1000);
            });
            
            // Open OAuth popup
            window.open(oauthData.authorizationUrl, 'oauth-popup', 'width=500,height=600');
            
            // Wait for OAuth callback
            const result = await oauthPromise;
            
            // Close modal
            this.update({ showOAuthModal: false });
            
            if (result.success !== false) {
              // OAuth succeeded, retry the original action
              const callback = this.state.oauthErrorCallback;
              if (callback && typeof callback === 'function') {
                await callback();
              }
            } else if (result.timeout) {
              alert(this.locale.t('alert_oauth_timeout'));
            } else {
              alert(this.locale.t('alert_oauth_failed') + ': ' + (result.error || 'Unknown error'));
            }
          } else {
            alert(this.locale.t('alert_oauth_url_failed'));
          }
        } catch (error) {
          console.error('Failed to re-authorize:', error);
          alert(this.locale.t('alert_oauth_reauth_failed') + ': ' + (error.message || 'Unknown error'));
        }
      },

      closeOAuthModal() {
        this.update({ showOAuthModal: false });
      },

      handleSearch(query) {
        this.update({ searchQuery: query });
        this.filterDrives(query);
      },

      clearFilter() {
        this.update({ searchQuery: '' });
        this.filterDrives('');
      },

      filterDrives(query) {
        const searchQuery = query.toLowerCase();
        if (!searchQuery) {
          // No query - show all drives
          this.update({ drives: this.state.allDrives });
          return;
        }

        // Filter drives by name
        const filteredDrives = this.state.allDrives.filter(drive =>
          drive.name?.toLowerCase().includes(searchQuery)
        );
        this.update({ drives: filteredDrives });
      },

    }
  </script>

  <style>
    :scope {
      --drive-item-transition: all 0.2s ease;
    }

    .shared-drive-selector-container {
      font-family: inherit;
    }

    /* Container for scrollable drives list */
    .drives-list-container {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    /* Scrollable area with max height */
    .drives-list-scroll {
      flex: 1;
      overflow-y: auto;
      padding-right: 8px; /* Account for scrollbar */
    }

    /* Sticky buttons at bottom */
    .sticky-action-buttons {
      flex-shrink: 0;
      margin-top: auto;
      box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    }

    .drive-item {
      transition: var(--drive-item-transition);
      background-attachment: fixed;
      position: relative;
    }

    .drive-item:hover {
      transform: translateX(4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .drive-overlay {
      backdrop-filter: blur(0.5px);
    }

    .drive-item input[type="radio"]:checked {
      accent-color: hsl(var(--pc));
    }

    /* Text styling for readability over background images */
    .drive-item h4 {
      text-shadow:
        0 1px 2px rgba(0, 0, 0, 0.8),
        0 2px 4px rgba(0, 0, 0, 0.6);
    }

    .drive-item p {
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
    }
  </style>
</shared-drive-selector>
